import { useEffect, useRef } from 'react'
import { Navigate, NavLink, useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { profileApi, resumeApi } from '@/api/endpoints'
import { SubscriptionTab } from '@/features/billing/SubscriptionTab'
import { EmptyState, PageSpinner } from '@/components/ui/Feedback'
import { ProgressBar } from '@/components/ui/Page'
import { cn } from '@/lib/utils'
import type { Profile } from '@/types/api'

import { PortalsTab } from './PortalsTab'
import { PreferencesTab } from './PreferencesTab'
import { PreviewTab } from './PreviewTab'
import { ProfileTab } from './ProfileTab'
import { Recommendations } from './Recommendations'
import { SkillsTab } from './SkillsTab'
import { WorkHistoryTab } from './WorkHistoryTab'
import { missingChips, profileCompleteness, type Completeness } from './completeness'
import { PROFILE_TABS, initials, tabHref, type ProfileTabKey } from './shared'

const TAB_FOR_PARAM: Record<string, ProfileTabKey> = {
  skills: 'skills',
  'work-history': 'work-history',
  preferences: 'preferences',
  portals: 'portals',
  preview: 'preview',
  subscription: 'subscription',
}

/** The app header is 56px tall; the tab row sticks right under it on phones. */
const STICKY_TOP = 'top-[calc(56px+var(--safe-top))]'

export function ProfilePage() {
  const { tab: tabParam } = useParams()
  const location = useLocation()
  const tab: ProfileTabKey | null = tabParam ? TAB_FOR_PARAM[tabParam] ?? null : 'profile'

  const profileQuery = useQuery({ queryKey: ['profile'], queryFn: profileApi.get })
  const resumesQuery = useQuery({ queryKey: ['resumes'], queryFn: resumeApi.list })
  // Optional: the page is complete without it, so a failure stays silent.
  const recommendationsQuery = useQuery({
    queryKey: ['profile-recommendations'],
    queryFn: profileApi.recommendations,
    retry: false,
    staleTime: 60_000,
  })

  // A "+ Add X" or a recommendation lands on a section or input: scroll there
  // once the tab has rendered, and put the caret in it when it is an input.
  const loaded = Boolean(profileQuery.data)
  useEffect(() => {
    if (!loaded || !location.hash) return
    const id = location.hash.slice(1)
    const timer = window.setTimeout(() => {
      const target = document.getElementById(id)
      if (!target) return
      const isInput = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
      target.scrollIntoView({ behavior: 'smooth', block: isInput ? 'center' : 'start' })
      if (isInput) target.focus({ preventScroll: true })
    }, 60)
    return () => window.clearTimeout(timer)
  }, [loaded, location.key, location.hash])

  // On a phone the tab row scrolls sideways: keep the open tab in view.
  const tabsRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const row = tabsRef.current
    const active = row?.querySelector<HTMLElement>('[aria-current="page"]')
    if (!row || !active) return
    const left = active.offsetLeft
    const right = left + active.offsetWidth
    if (left < row.scrollLeft || right > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = Math.max(left - 16, 0)
    }
  }, [tab, loaded])

  if (!tab) return <Navigate to={tabHref('profile')} replace />
  if (profileQuery.isLoading) return <PageSpinner label="Loading your profile" />
  const profile = profileQuery.data
  if (!profile) return <EmptyState title="Profile unavailable" />

  const recommendations = Array.isArray(recommendationsQuery.data) ? recommendationsQuery.data : []
  const completeness = profileCompleteness(
    profile,
    resumesQuery.isLoading ? null : resumesQuery.data?.length ?? 0,
    recommendations,
  )
  // A dot on a tab that still has a required fact to add.
  const tabsMissing = new Set(completeness.missing.map((item) => item.tab))
  // The same number the "details to add" panel shows.
  const todo = recommendations.length + missingChips(completeness.missing, recommendations).length

  return (
    <div className="space-y-4">
      <Header profile={profile} completeness={completeness} todo={todo} />

      <Recommendations items={recommendations} profile={profile} missing={completeness.missing} />

      {/* Tabs: a segmented row that scrolls sideways and sticks under the app header on phones. */}
      <nav
        ref={tabsRef}
        aria-label="Profile sections"
        className={cn(
          'hscroll sticky z-20 -mx-4 gap-1 bg-canvas/95 px-4 py-2 backdrop-blur-md md:static md:mx-0 md:flex-wrap md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-none',
          STICKY_TOP,
        )}
      >
        <div className="inline-flex shrink-0 gap-0.5 rounded-[11px] border border-line bg-surface-muted p-0.5">
          {PROFILE_TABS.map((entry) => (
            <NavLink
              key={entry.key}
              to={entry.path}
              end
              className={({ isActive }) =>
                cn(
                  'relative inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[9px] px-3.5 text-[13.5px] font-medium transition-colors',
                  isActive ? 'bg-brand-soft text-brand ring-1 ring-brand/15 shadow-sm' : 'text-ink-muted hover:text-ink',
                )
              }
            >
              <>
                {entry.label}
                {tabsMissing.has(entry.key) && (
                  <span
                    aria-label="Details to add"
                    title="Details to add"
                    className="h-1.5 w-1.5 rounded-full bg-warning"
                  />
                )}
              </>
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="min-w-0">
        {tab === 'profile' && <ProfileTab profile={profile} resumes={resumesQuery.data} />}
        {tab === 'skills' && <SkillsTab profile={profile} />}
        {tab === 'work-history' && <WorkHistoryTab profile={profile} />}
        {tab === 'preferences' && <PreferencesTab profile={profile} />}
        {tab === 'portals' && <PortalsTab />}
        {tab === 'preview' && <PreviewTab profile={profile} />}
        {tab === 'subscription' && <SubscriptionTab />}
      </div>
    </div>
  )
}

/** Name, headline, where they are, and how complete the profile is. */
function Header({ profile, completeness, todo }: { profile: Profile; completeness: Completeness; todo: number }) {
  const name = profile.fullName ?? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
  const subtitle =
    profile.headline || [profile.currentTitle, profile.currentCompany].filter(Boolean).join(' · ')
  const locationLine = [profile.location?.city, profile.location?.countryName].filter(Boolean).join(', ')
  const tone = completeness.percent >= 50 ? 'brand' : 'warning'

  return (
    <header className="page-heading card flex flex-col gap-5 p-5 sm:p-6 md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 items-center gap-3.5">
        <span
          aria-hidden
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-brand text-[17px] font-semibold tracking-tight text-oncolor sm:h-14 sm:w-14 sm:text-[19px]"
        >
          {initials(profile)}
        </span>
        <div className="min-w-0">
          <p className="label-eyebrow">Profile</p>
          <h1 className="mt-0.5 truncate text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[26px]">
            {name || 'Add your name'}
          </h1>
          {(subtitle || locationLine) && (
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13.5px] text-ink-muted">
              {subtitle && <span className="line-clamp-1 text-ink-soft">{subtitle}</span>}
              {subtitle && locationLine && <span aria-hidden className="text-ink-faint">·</span>}
              {locationLine && <span>{locationLine}</span>}
            </p>
          )}
        </div>
      </div>

      <div className="w-full md:w-[260px]" aria-label="Profile completeness">
        <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
          <span className="font-medium text-ink-muted">Profile completeness</span>
          <span className="num text-[14px] font-semibold text-ink">{completeness.percent}%</span>
        </div>
        <ProgressBar value={completeness.percent} tone={tone} label="Profile completeness" className="mt-1.5" />
        <p className="mt-1.5 text-[12px] text-ink-faint">
          {completeness.percent >= 90
            ? 'Your profile is complete.'
            : todo > 0
              ? `${todo} ${todo === 1 ? 'detail' : 'details'} to add. Each one fills more forms for you.`
              : 'A few more details help us fill more forms.'}
        </p>
      </div>
    </header>
  )
}
